import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { LinkType } from "@/lib/ticketLinks";
import type { RowLinks, UUID } from "@/lib/types";

const EMPTY: RowLinks = { parent: null, follow_ups: [], related: [] };

// Read + manage a ticket's links (peer relations) and Folgetickets. A link writes a
// row.ticket_linked Verlauf event; a Folgeticket creates a new board row — so we
// invalidate the links query, the Verlauf, and (for follow-ups) the board.
export function useRowLinks(rowId: UUID | undefined, boardId: UUID | undefined, enabled = true) {
  const qc = useQueryClient();

  const list = useQuery({
    enabled: Boolean(rowId) && enabled,
    queryKey: rowId ? queryKeys.rowLinks(rowId) : ["rows", "void", "links"],
    queryFn: () => api.getRowLinks(rowId as UUID),
  });

  function invalidate() {
    if (rowId) void qc.invalidateQueries({ queryKey: queryKeys.rowLinks(rowId) });
    if (rowId) void qc.invalidateQueries({ queryKey: queryKeys.rowActivity(rowId) });
    // The candidate picker excludes already-linked tickets — refresh it too.
    if (rowId) void qc.invalidateQueries({ queryKey: ["rows", rowId, "link-candidates"] });
    if (boardId) void qc.invalidateQueries({ queryKey: queryKeys.board(boardId) });
  }

  const createLink = useMutation<RowLinks, Error, { targetRowId: UUID; linkType: LinkType }>({
    mutationFn: ({ targetRowId, linkType }) =>
      api.createTicketLink(rowId as UUID, { target_row_id: targetRowId, link_type: linkType }),
    onSuccess: invalidate,
  });

  const createFollowUp = useMutation<
    { row_id: UUID; links: RowLinks },
    Error,
    { cells: Record<string, unknown> }
  >({
    mutationFn: ({ cells }) => api.createFollowUp(rowId as UUID, { cells }),
    onSuccess: invalidate,
  });

  const removeLink = useMutation<void, Error, UUID>({
    mutationFn: (linkId) => api.deleteTicketLink(linkId),
    onSuccess: invalidate,
  });

  return {
    links: list.data ?? EMPTY,
    isLoading: list.isLoading,
    createLink,
    createFollowUp,
    removeLink,
  };
}
